import { useTranslation } from 'next-i18next/pages'
import { CustomerFlows, DeliveryAddressFieldKeys, InputGroupKeys } from 'store/customer/enums'
import { nextStep, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { trackEvent, trackGTMEvent } from 'utils/tracking'
import { Form, Heading, ToggleRadio } from '@boltenergy-be/design-system'
import { useForm } from 'react-hook-form'
import { EnergyType } from 'components/start-simulation-card/types'
import { EnergyTypeInputs } from './types'
import SubmitButton from 'features/simulation/components/submit-button/SubmitButton'

const EnergyTypeStep = () => {
  // Redux store
  const { inputs } = useStoreSelector((store) => store.customer)
  const { needsGas } = inputs[InputGroupKeys.DELIVERY_ADDRESS]
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['simulation', 'common'])

  // REACT HOOK FORM
  const { register, handleSubmit, formState, setValue } = useForm<EnergyTypeInputs>({
    defaultValues: {
      energyType: needsGas ? EnergyType.ELECTRICITY_GAS : EnergyType.ELECTRICITY
    },
    mode: 'onChange'
  })

  /**
   * Saves the simulation step & goes to the next step
   */
  const submit = (data: EnergyTypeInputs) => {
    // Track 'Simulation Started' event @Semetis
    trackGTMEvent('simulation_step_finished:energy-code', {
      product_need: data.energyType === EnergyType.ELECTRICITY_GAS ? 'gaz + electricity' : 'electricity'
    })
    trackEvent({ category: 'simulation', object: 'energy_type_step', action: 'finish' })

    dispatch(nextStep({ flow: CustomerFlows.SIMULATION }))
  }

  return (
    <>
      <Heading as="h1" variant="h3" serif>
        {t('simulation:steps.energyType.title')}
      </Heading>

      <Form onSubmit={handleSubmit(submit)}>
        <ToggleRadio
          label={t('simulation:steps.energyType.title')}
          hiddenLabel
          grid="full"
          name="userType"
          error={formState?.errors?.energyType?.message}
          options={[
            {
              ...register('energyType', { required: t('validation:required') }),
              value: EnergyType.ELECTRICITY,
              id: EnergyType.ELECTRICITY,
              label: t('common:electricity'),
              onChange: () => {
                setValue('energyType', EnergyType.ELECTRICITY)
                dispatch(
                  updateInput({
                    group: InputGroupKeys.DELIVERY_ADDRESS,
                    key: DeliveryAddressFieldKeys.NEEDS_GAS,
                    value: false
                  })
                )
              }
            },
            {
              ...register('energyType', { required: t('validation:required') }),
              value: EnergyType.ELECTRICITY_GAS,
              id: EnergyType.ELECTRICITY_GAS,
              label: t('common:electricityAndGas'),
              onChange: () => {
                setValue('energyType', EnergyType.ELECTRICITY_GAS)
                dispatch(
                  updateInput({
                    group: InputGroupKeys.DELIVERY_ADDRESS,
                    key: DeliveryAddressFieldKeys.NEEDS_GAS,
                    value: true
                  })
                )
              }
            }
          ]}
        />

        <Form.Footer>
          <SubmitButton />
        </Form.Footer>
      </Form>
    </>
  )
}

export default EnergyTypeStep
